/**
 * UI-8f Task 6 — ResolveDialog.
 *
 * Modal dialog triggered from IncidentDetailPage when an INVESTIGATING
 * incident is moved to RESOLVED. The backend requires a non-empty
 * `responseActions` string when `to === 'RESOLVED'`, so Submit stays
 * disabled until the operator types something.
 *
 * Sibling: CloseDialog (same shape, lessonsLearned).
 */
import { useEffect, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputTextarea } from 'primereact/inputtextarea'

export interface ResolveDialogProps {
  open: boolean
  onClose: () => void
  /** Called with the trimmed responseActions text. */
  onSubmit: (text: string) => Promise<void>
}

export function ResolveDialog({
  open,
  onClose,
  onSubmit,
}: ResolveDialogProps): JSX.Element {
  const [responseActions, setResponseActions] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Reset on close so the next open starts clean.
  useEffect(() => {
    if (!open) {
      setResponseActions('')
      setSubmitting(false)
    }
  }, [open])

  const trimmed = responseActions.trim()
  const valid = trimmed.length > 0

  async function handleSubmit(): Promise<void> {
    if (!valid) return
    setSubmitting(true)
    try {
      await onSubmit(trimmed)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      header="Resolve Incident"
      visible={open}
      onHide={onClose}
      style={{ width: '32rem' }}
    >
      <div className="flex flex-col gap-3">
        <label
          htmlFor="resolve-response-actions"
          className="text-sm font-medium"
        >
          Response Actions <span className="text-red-600">*</span>
        </label>
        <InputTextarea
          id="resolve-response-actions"
          value={responseActions}
          onChange={(e) => {
            setResponseActions(e.target.value)
          }}
          rows={6}
          maxLength={5000}
          placeholder="What did you do to resolve this incident?"
        />
        <div className="flex justify-end gap-2">
          <Button
            label="Cancel"
            text
            severity="secondary"
            onClick={onClose}
            disabled={submitting}
          />
          <Button
            label="Resolve"
            disabled={!valid || submitting}
            loading={submitting}
            onClick={() => {
              void handleSubmit()
            }}
          />
        </div>
      </div>
    </Dialog>
  )
}

export default ResolveDialog
